import TextField from "@/components/common/text-field/TextField.vue";
import Pagination from "@/components/common/table-field/Pagination.vue";
import TableColumnChooser from "@/components/common/table-field/TableColumnChooser.vue";
import MobileTable from "@/components/common/table-field/MobileTable.vue";
import TableHeader from "@/components/common/table-field/TableHeader.vue";
export default {
    name: 'TableField',
    components: {
        TableColumnChooser,
        TextField,
        Pagination,
        MobileTable,
        TableHeader
    },

    emits: {
        "update:params": (val) => true,
        "update:data": (val) => true,
        "fetch-data": (val) => true,
        "sort-change": (val) => true,
        "action": (row) => true,
        "row-click": (row) => true,
        "onCreate": () => true
    },

    props: {
        data: { type: Array, default: () => [] },
        columns: { type: Array, default: () => [] },
        params: {
            type: Object,
            default: () => ({
                page: 1,
                pageSize: 10,
                search: null,
                sort_column: null,
                sort_direction: null,
            })
        },
        showPageSize: { type: Boolean, default: true },
        rowClass: { type: Function, default: () => "" },
        cellClass: { type: Function, default: () => "" },
        local: { type: Boolean, default: false },
        showSearch: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        columnChooser: { type: Boolean, default: true },
        section: { type: String, default: null, validator: val => val === null || typeof val === 'string' },
        mobileDevices: { type: Boolean, default: false },
        stickyHeader: { type: Boolean, default: false },
        rowClick: { type: Boolean, default: false },
        showCreate: { type: Boolean, default: false },
        storeSearch: { type: Boolean, default: false },
        enablePagination: { type: Boolean, default: null }
    },

    data() {
        return {
            initialized: false,
            firstRender: true,
            visibleColumnsState: {},
            columnVisibility: {},
            isMobileActive: false,
            overflowMap: new Map(),
            tooltipConfig: {
                color: '#fff',
                placement: 'top',
                backgroundColor: '#000000'
            }
        }
    },

    computed: {
        filteredData() {
            if (!this.local) return this.data;
            if (!this.search) return this.data;

            const s = String(this.search).toLowerCase();
            return this.data.filter(row =>
                Object.values(row).some(v => String(v ?? '').toLowerCase().includes(s))
            );
        },

        paginatedData() {
            if (!this.local) return this.data;
            const start = (this.page - 1) * this.pageSize;
            return this.sortedData.slice(start, start + this.pageSize);
        },

        sortKey() {
            return this.params.sort_column || null;
        },
        sortOrder() {
            return this.params.sort_direction || null;
        },

        sortedData() {
            if (!this.local) return this.filteredData;
            if (!this.sortKey) return this.filteredData;

            return [...this.filteredData].sort((a, b) => {
                const aVal = a[this.sortKey];
                const bVal = b[this.sortKey];

                if (aVal == null) return 1;
                if (bVal == null) return -1;

                if (typeof aVal === 'string') {
                    return this.sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                }

                return this.sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
            });
        },

        page: {
            get() { return this.params.page || 1; },
            set(val) { this.$emit("update:params", { ...this.params, page: val }); }
        },

        pageSize: {
            get() { return this.params.pageSize != null ? Number(this.params.pageSize) : 20; },
            set(val) { this.$emit("update:params", { ...this.params, pageSize: val, page: 1 }); }
        },

        search: {
            get() { return this.params.search ?? ""; },
            set(val) { this.$emit("update:params", { ...this.params, search: val }); }
        },

        totalPages() {
            if (this.local) {
                return Math.ceil(this.sortedData.length / this.pageSize) || 1;
            }

            return this.params.last_page || 1;
        },

        visiblePages() {
            const total = this.totalPages;
            const current = this.page;
            const maxVisible = 5;
            let start = Math.max(current - 2, 1);
            let end = Math.min(start + maxVisible - 1, total);
            start = Math.max(end - maxVisible + 1, 1);

            const pages = [];
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            return pages;
        },

        lastVisibleColumnKey() {
            const visibleCols = this.columns.filter(c => c && this.visibleColumnsState[c.key]);
            return visibleCols.length ? visibleCols[visibleCols.length - 1].key : null;
        },

        hasParams() {
            if (this.enablePagination !== null) {
                return this.enablePagination;
            }

            return this.local || !!this.params;
        },
    },

    methods: {
        setCellRef(el, rowIndex, col, colIndex) {
            if (!el || col.key === 'actions') return;

            const key = `${rowIndex}-${colIndex}`;

            this.$nextTick(() => {
                this.overflowMap.set(key, el.scrollWidth > el.clientWidth);
            });
        },
        getTooltip(row, rowIndex, col, colIndex) {
            if (col.key === 'actions') return null;
            const value = row[col.key];
            if (!value) return null;
            if (col.wrap !== false) return null;

            return {
                text: value,
                key: `${rowIndex}-${colIndex}`,
                onlyOverflow: true,
                theme: this.tooltipConfig
            }
        },

        recalculateOverflow() {
            this.$nextTick(() => {
                this.overflowMap.clear();
            });
        },

        handleFilterChange({ key, value }) {
            const newFilter = { ...(this.params.filter || {}) };
            if (value === null || value === '' || value === undefined) {
                delete newFilter[key];
            } else {
                newFilter[key] = value;
            }

            const updatedParams = { ...this.params, filter: newFilter, page: 1 };
            this.$emit('update:params', updatedParams);
            if (!this.local) {
                this.$emit('fetch-data', updatedParams);
            }
        },

        handleRowClick(row, col) {
            if (!this.rowClick) return;
            if (col && col.rowClick === false) return;

            const clickableColumns = this.columns.filter(c => c.rowClick === true);
            if (clickableColumns.length) {
                const allowed = clickableColumns.some(c => c.key === col.key);
                if (!allowed) return;
            }

            this.$emit('row-click', row);
        },

        handleCreate() {
            this.$emit('onCreate');
        },

        emitData() {
            if (this.local) {
                this.$emit("update:data", {
                    filtered: this.filteredData,
                    sorted: this.sortedData,
                    paginated: this.paginatedData,
                    total: this.sortedData.length
                });
            } else {
                this.$emit("fetch-data", {
                    page: this.page,
                    pageSize: this.pageSize,
                    search: this.search,
                    sortKey: this.sortKey,
                    sortOrder: this.sortOrder
                });
            }
        },

        sortBy(col) {
            if (!col.sortable) return;
            let newSortOrder = 'asc';
            if (this.params.sort_column === col.key) {
                newSortOrder = this.params.sort_direction === 'asc' ? 'desc' : 'asc';
            }

            const newParams = {
                ...this.params,
                sort_column: col.key,
                sort_direction: newSortOrder,
                page: 1
            };

            this.$emit('update:params', newParams);

            if (!this.local) {
                this.$emit("fetch-data", {
                    page: 1,
                    pageSize: this.pageSize,
                    search: this.search,
                    sortKey: col.key,
                    sortOrder: newSortOrder
                });
            }
        },

        changePage(p) {
            if (p < 1 || p > this.totalPages) return;
            this.$emit("update:params", { ...this.params, page: p });

            if (!this.local) {
                this.$emit("fetch-data", {
                    page: p,
                    pageSize: this.pageSize,
                    search: this.search,
                    sortKey: this.sortKey,
                    sortOrder: this.sortOrder
                });
            }
        },

        changePageSize(size) {
            this.$emit("update:params", { ...this.params, pageSize: size, page: 1 });
            if (!this.local) {
                this.$emit("fetch-data", {
                    page: 1,
                    pageSize: size,
                    search: this.search,
                    sortKey: this.sortKey,
                    sortOrder: this.sortOrder
                });
            }
        },

        updateSearch(value) {
            const newParams = { ...this.params, search: value, page: 1 };
            this.$emit("update:params", newParams);

            if (this.storeSearch) {
                const key = `${this.section}_params_search`;
                if (value && value !== '') {
                    localStorage.setItem(key, value);
                } else {
                    localStorage.removeItem(key);
                }
            }

            if (!this.local) {
                this.$emit("fetch-data", {
                    page: 1,
                    pageSize: this.pageSize,
                    search: value,
                    sortKey: this.sortKey,
                    sortOrder: this.sortOrder
                });
            }
        },

        showColumnChooser() {
            this.$refs.showActionColumnChooser.show(this.columns, this.section);
        },

        handleColumnChange(updatedColumns) {
            const newVisibility = {};
            updatedColumns.forEach(col => {
                newVisibility[col.key] = col.hide ?? false;
            });
            this.visibleColumnsState = {};
            Object.keys(newVisibility).forEach(key => {
                this.visibleColumnsState[key] = !newVisibility[key];
            });
        },

        handleActionClick(act, row) {
            this.$emit('action', {
                type: act.emit,
                row: row
            });
        },

        checkMobile() {
            if (typeof window !== "undefined") {
                this.isMobileActive = this.mobileDevices && window.innerWidth < 768;
            }
        },

        updateFilter(key, value) {
            if (!key) return;
            const newFilter = { ...(this.params.filter || {}) };
            if (value === null || value === '' || value === undefined) {
                delete newFilter[key];
            } else {
                newFilter[key] = value;
            }

            const updatedParams = { ...this.params, filter: newFilter, page: 1 };
            this.$emit('update:params', updatedParams);
            this.$emit('fetch-data', updatedParams);
        }
    },

    mounted() {
        this.initialized = true;

        if (this.columnChooser && !this.section) {
            throw new Error('[TableField]: columnChooser=true requires a passed prop section (for the server request)');
        }

        const savedColumns = localStorage.getItem(`${this.section}-columns`);
        this.columnVisibility = savedColumns ? JSON.parse(savedColumns) : {};

        this.columns.forEach(col => {
            if (col.key in this.columnVisibility) {
                this.visibleColumnsState[col.key] = !this.columnVisibility[col.key];
            } else {
                this.visibleColumnsState[col.key] = !col.hide;
            }
        });

        this.checkMobile();
        if (this.mobileDevices) {
            window.addEventListener('resize', this.checkMobile);
        }
        window.addEventListener('resize', this.recalculateOverflow);
    },

    beforeUnmount() {
        if (this.mobileDevices) {
            window.removeEventListener('resize', this.checkMobile);
        }
        window.removeEventListener('resize', this.recalculateOverflow);
    },

    watch: {
        params: {
            handler(newParams) {
                if (this.firstRender) {
                    this.firstRender = false;
                    return;
                }
                if (!this.initialized) return;
                if (this.local) {
                    this.emitData();
                }
            },
            deep: true
        },
        data: {
            handler() {
                if (!this.initialized || !this.local) return;
                this.emitData();
            },
            deep: true
        },
        paginatedData() {
            this.overflowMap.clear();
        },
        columns: {
            handler(newCols) {
                const newVisibility = {};

                newCols.forEach(col => {
                    if (!col || !col.key) return;

                    if (col.key in this.columnVisibility) {
                        newVisibility[col.key] = !this.columnVisibility[col.key];
                    } else {
                        newVisibility[col.key] = !col.hide;
                    }
                });

                this.visibleColumnsState = newVisibility;
            },
            deep: true,
            immediate: true
        }
    },
}